import { ReactNode } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import StyleIcon from '@mui/icons-material/Style';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import '../Home/DynamicHomepage/components/Landing/landing.css';
import CardCatalog, { liftSx } from './CardCatalog';

const ART =
  'https://incrypt-dynamic-website-images.s3.ap-southeast-2.amazonaws.com';

const FAN_CARDS = ['Natheel', 'Nebula', 'Phryis', 'Raine', 'Syprus'].map(
  (n) => `${ART}/${n}.png`
);

// Arc geometry — x/y offsets are multiples of the (responsive) card width so the
// whole fan scales cleanly. Centre card sits upright and on top; outer cards
// rotate outward and drop down.
const FAN = [
  { rot: -20, x: -1.44, y: 0.36 },
  { rot: -10, x: -0.76, y: 0.09 },
  { rot: 0, x: 0, y: -0.02 },
  { rot: 10, x: 0.76, y: 0.09 },
  { rot: 20, x: 1.44, y: 0.36 }
];

const UTILITIES = [
  {
    icon: <StyleIcon />,
    title: 'Trading Card Game',
    body: 'Build decks and battle for rank in the Incrypt arena.',
    // the one tile with a public page of its own to go to
    to: '/trading-card-game'
  },
  {
    icon: <SportsEsportsIcon />,
    title: 'RPG Gear & Characters',
    body: 'Equip them and play through Shadows of Satoshi.'
  },
  {
    icon: <StorefrontIcon />,
    title: 'Tradeable',
    body: 'Buy, sell, and trade on the in-portal marketplace.'
  },
  {
    icon: <AutoAwesomeIcon />,
    title: 'Real Utility',
    body: 'Every collectible earns its place across the ecosystem.'
  }
];

const kickerSx = {
  font: '800 11px ui-monospace, Menlo, Consolas, monospace',
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#38e8ff'
} as const;

const tileSx = {
  p: { xs: 2.5, md: 3 },
  borderRadius: '16px',
  background: 'linear-gradient(180deg, rgba(13,20,29,0.9), rgba(7,11,17,0.95))',
  border: '1px solid rgba(140,170,190,0.14)'
} as const;

const linkTileSx = {
  display: 'block',
  color: 'inherit',
  textDecoration: 'none',
  '&:focus-visible': {
    outline: '2px solid #38e8ff',
    outlineOffset: '3px',
    borderColor: 'rgba(56,232,255,0.45)'
  },
  '& .cue-arrow': { display: 'inline-block', transition: 'transform 0.18s ease' },
  '&:hover .cue-arrow, &:focus-visible .cue-arrow': { transform: 'translateX(3px)' }
} as const;

/* A tile with somewhere to go is a real link (keyboard-reachable, opens in a new
   tab), and only that tile lifts and takes focus — the rest are statements, not
   destinations, and stay exactly as they were. */
const UtilityTile = ({ to, children }: { to?: string; children: ReactNode }) =>
  to ? (
    <Box component={Link} to={to} sx={[tileSx, liftSx, linkTileSx]}>
      {children}
    </Box>
  ) : (
    <Box sx={tileSx}>{children}</Box>
  );

const DigitalCollectibles = () => {
  const isSm = useMediaQuery('(max-width:600px)');
  const isMd = useMediaQuery('(max-width:960px)');
  const CW = isSm ? 106 : isMd ? 150 : 190; // card width (px)
  const CH = (CW * 1150) / 700; // match the 700×1150 card art (was 4/3, which cropped it)
  // Horizontal spread of the fan — tighter on small screens so the outer cards
  // don't run off the edge.
  const spread = isSm ? 0.6 : isMd ? 0.85 : 1;

  return (
    <Box
      sx={{
        background: '#070c13',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <SiteHeader />

      <Box component="section" sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Ambient glows */}
        <Box
          className="inc-glow"
          sx={{
            position: 'absolute',
            top: '-8%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 720,
            height: 460,
            pointerEvents: 'none',
            background:
              'radial-gradient(circle, rgba(56,232,255,0.16), transparent 62%)'
          }}
        />

        <Box
          sx={{
            position: 'relative',
            maxWidth: 1100,
            mx: 'auto',
            px: { xs: 2.5, md: 3 },
            pt: { xs: 4, md: 7 },
            pb: { xs: 5, md: 7 },
            textAlign: 'center'
          }}
        >
          {/* Fanned card display */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: CH * 1.42,
              mb: { xs: 2, md: 3 }
            }}
          >
            {FAN.map((f, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '46%',
                  width: CW,
                  aspectRatio: '700 / 1150',
                  transform: `translate(calc(-50% + ${f.x * CW * spread}px), calc(-50% + ${f.y * CW}px)) rotate(${f.rot}deg)`,
                  zIndex: 5 - Math.abs(i - 2),
                  borderRadius: '14px',
                  overflow: 'hidden',
                  border: '1px solid rgba(160,190,210,0.25)',
                  boxShadow: '0 30px 60px -24px rgba(0,0,0,0.85)',
                  background: 'linear-gradient(180deg, #0e1622, #070c13)'
                }}
              >
                <Box
                  component="img"
                  src={FAN_CARDS[i]}
                  alt=""
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(56,232,255,0.10) 0%, transparent 30%, rgba(7,12,19,0.5) 100%)',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)'
                  }}
                />
              </Box>
            ))}
          </Box>

          <Typography sx={kickerSx}>Digital Collectibles</Typography>
          <Typography
            className="inc-orbitron"
            sx={{
              mt: 1.5,
              fontSize: { xs: 32, sm: 42, md: 54 },
              fontWeight: 900,
              lineHeight: 1.05,
              color: '#eef6fb'
            }}
          >
            Our Digital Collectibles
          </Typography>
          <Typography
            sx={{
              mt: 2.5,
              maxWidth: 760,
              mx: 'auto',
              fontSize: { xs: 15.5, md: 17 },
              lineHeight: 1.65,
              color: '#9fb2c0'
            }}
          >
            Incrypt Digital Collectibles are more than collectibles — they're key
            assets in the Incrypt universe. From Trading Card Game packs to RPG
            gear and characters, each one carries real, usable utility. Equip
            them, trade them, and compete as you unlock their full potential
            across the ecosystem.
          </Typography>
        </Box>

        {/* Utility highlights */}
        <Box
          sx={{
            position: 'relative',
            maxWidth: 1100,
            mx: 'auto',
            px: { xs: 2.5, md: 3 },
            pb: { xs: 6, md: 9 }
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)'
              },
              gap: { xs: 2, md: 2.5 }
            }}
          >
            {UTILITIES.map((u) => (
              <UtilityTile key={u.title} to={u.to}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '12px',
                    color: '#38e8ff',
                    background: 'rgba(56,232,255,0.1)',
                    border: '1px solid rgba(56,232,255,0.28)'
                  }}
                >
                  {u.icon}
                </Box>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: 16.5,
                    fontWeight: 800,
                    color: '#eef6fb'
                  }}
                >
                  {u.title}
                </Typography>
                <Typography
                  sx={{ mt: 0.75, fontSize: 14, lineHeight: 1.55, color: '#9fb2c0' }}
                >
                  {u.body}
                </Typography>
                {u.to && (
                  <Typography
                    sx={{
                      mt: 1.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      fontSize: 13.5,
                      fontWeight: 800,
                      color: '#38e8ff'
                    }}
                  >
                    Play the game
                    <Box component="span" className="cue-arrow" aria-hidden>
                      →
                    </Box>
                  </Typography>
                )}
              </UtilityTile>
            ))}
          </Box>
        </Box>
      </Box>

      <CardCatalog />

      <RaffleFooter />
    </Box>
  );
};

export default DigitalCollectibles;
